<script setup lang="ts">
import type {MediaPageData} from '~/types/media/MediaPageData'
import type {CapturePageData} from '~/types/media/CapturePageData'
import Dialog from "~/components/molecules/Dialog.vue"
import {useClipboard} from "@vueuse/core"
import {useDomainDataStore} from "~/stores/domain"
import {useUserStore} from '~/stores/user'

const shareUrl = ref<string|null>(null)
const directoryUrl = ref<string|null>(null)
const flippedDirectoryCreator = ref<typeof Dialog|null>(null)
const { isSupported: clipboardIsSupported, copy, copied } = useClipboard()
const domainStore = useDomainDataStore()

const props = defineProps<{
  media: MediaPageData|CapturePageData,
}>()

function reset() {
  shareUrl.value = null
  directoryUrl.value = null
  flippedDirectoryCreator.value?.reset()
}

function onShareUrlRequested(directoryCode: string) {
  shareUrl.value = shareUrl.value = domainStore.getDomainHome() + useNuxtApp().$localePath({name: 'collection-code', params: {code: directoryCode}})
  directoryUrl.value = useNuxtApp().$localePath({name: 'collection-code', params: { code: directoryCode }})
}

defineExpose({
  reset
})

watch(copied, (copied) => {
  if (copied) {
    showSuccessToast(useNuxtApp().$i18n.t('_global.copied'))
  }
})

const flipTermsPage = computed(() => {
  return useUserStore().getPublicPageWithEnglishSlug('flip-terms')
})

const descriptionExpanded = ref(false)
</script>

<template>
    <div class="bg-white rounded-bento p-6">
      <AtomsHeadingsLarge class="mb-6">{{ $t('flip.authorize_flips') }}</AtomsHeadingsLarge>
      <template v-if="shareUrl" >
        <div class="block w-full">
          <div class="flex items-end gap-4 mb-4">
            <AtomsFormTextInput :label="$t('flip.share_with_students')" class="grow" :model-value="shareUrl" :readonly="true" />
            <div v-if="clipboardIsSupported">
              <AtomsButton size="small" :label="$t('_global.copy')" class="mb-1 !p-[11px]" @click="copy(shareUrl)" />
            </div>
          </div>
          <AtomsButton :label="$t('flip.go_to_file')" dark-color="domain" :to="directoryUrl" />
        </div>
      </template>
      <template v-else>
        <p class="block w-full max-w-lg mb-4" v-html="t('flip.description', {mediaTitle: '<b>' + media.title + '</b>'})"></p>
        <LayoutsSlideToggler :open="!descriptionExpanded">
          <div class="flex items-center gap-4">
            <div class="grow">
              <AtomsButton :label="$t('_global.learn_more')" size="small" @click="descriptionExpanded = true" />
            </div>
          </div>
        </LayoutsSlideToggler>
        <LayoutsSlideToggler :open="descriptionExpanded">
          <div class="max-w-prose">
            <p class="mb-4">{{ $t('flip.more_description.part_1') }}</p>
            <p v-if="flipTermsPage">
              <NuxtLink :to="localePath({name: 'page-slug', params: {slug: flipTermsPage.slug}})" class="underline">
                {{ $t('flip.more_description.part_2') }}</NuxtLink>
            </p>
          </div>
        </LayoutsSlideToggler>
        <MoleculesFlippedDirectoryCreator ref="flippedDirectoryCreator" :media-data="media" @share-url-requested="onShareUrlRequested"/>
      </template>
    </div>
</template>